import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, ArrowRightLeft, CheckCircle2, XCircle, RefreshCw,
  AlertTriangle, Archive, Wand2, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_META = {
  auto_convert: {
    label: "Auto-Convert",
    desc: "High confidence these were used as surveys — safe to convert.",
    tone: { bg: "color-mix(in srgb, #0d9488 12%, transparent)", border: "#0d9488", text: "#0d9488" },
    icon: Wand2,
  },
  review: {
    label: "Needs Review",
    desc: "Ambiguous — decide individually.",
    tone: { bg: "color-mix(in srgb, #d97706 12%, transparent)", border: "#d97706", text: "#d97706" },
    icon: AlertTriangle,
  },
  keep: {
    label: "Keep as Assessment",
    desc: "Has scored items or no per-question data.",
    tone: { bg: "var(--admin-bg)", border: "var(--admin-container-border)", text: "var(--admin-container-muted)" },
    icon: Archive,
  },
  converted: {
    label: "Converted",
    desc: "Already migrated to a Survey.",
    tone: { bg: "color-mix(in srgb, #6366f1 12%, transparent)", border: "#6366f1", text: "#6366f1" },
    icon: CheckCircle2,
  },
  kept: {
    label: "Kept (Admin Decided)",
    desc: "Admin chose to keep as assessment.",
    tone: { bg: "var(--admin-bg)", border: "var(--admin-container-border)", text: "var(--admin-container-muted)" },
    icon: XCircle,
  },
};

/**
 * Embeddable Survey Conversion panel (Phase 7 — folded into Survey Library).
 * Renders as a collapsible section. Caller is responsible for gating on the
 * `survey_conversion_enabled` flag — this component assumes the flag is on.
 */
export default function SurveyConversionSection() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [autoApplying, setAutoApplying] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/survey-conversion/queue`, { withCredentials: true });
      setQueue(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load conversion queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !queue) fetchQueue();
  }, [open, queue, fetchQueue]);

  const approve = async (entry) => {
    setActingId(entry.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/survey-conversion/${entry.id}/approve`, {}, { withCredentials: true },
      );
      toast.success(`Converted "${entry.name}" → Survey (${data.items_linked} items, ${data.placements_rewritten} placements rewritten)`);
      setConfirmApprove(null);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    } finally {
      setActingId(null);
    }
  };

  const reject = async (entry) => {
    setActingId(entry.id);
    try {
      await axios.post(`${API}/admin/survey-conversion/${entry.id}/reject`, {}, { withCredentials: true });
      toast.success(`Kept "${entry.name}" as assessment`);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setActingId(null);
    }
  };

  const autoApplyAll = async () => {
    setAutoApplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/survey-conversion/auto-apply`, {}, { withCredentials: true });
      toast.success(`${data.converted_count} auto-convert entries migrated`);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-apply failed");
    } finally {
      setAutoApplying(false);
    }
  };

  const renderBucketSection = (bucketKey) => {
    const meta = BUCKET_META[bucketKey];
    const rows = queue?.[bucketKey] || [];
    const Icon = meta.icon;
    return (
      <div
        key={bucketKey}
        className="rounded-lg border"
        style={{ backgroundColor: "var(--admin-bg)", borderColor: "var(--admin-container-border)" }}
        data-testid={`bucket-${bucketKey}`}
      >
        <div
          className="px-4 py-2.5 border-b flex items-center justify-between gap-3"
          style={{ borderColor: "var(--admin-container-border)" }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: meta.tone.bg, border: `1px solid ${meta.tone.border}` }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: meta.tone.text }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-xs font-heading font-bold" style={{ color: "var(--admin-container-text)" }}>
                  {meta.label}
                </h4>
                <Badge
                  className="text-[10px] font-body"
                  style={{ backgroundColor: meta.tone.bg, color: meta.tone.text, border: `1px solid ${meta.tone.border}` }}
                >
                  {rows.length}
                </Badge>
              </div>
              <p className="text-[10px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>{meta.desc}</p>
            </div>
          </div>
        </div>
        {rows.length === 0 ? (
          <p className="text-[11px] font-body text-center py-4" style={{ color: "var(--admin-container-muted)" }}>
            Empty
          </p>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--admin-container-border)" }}>
            {rows.map((entry) => (
              <div key={entry.id} className="px-4 py-2.5 grid grid-cols-12 gap-3 items-center" data-testid={`conv-row-${entry.id}`}>
                <div className="col-span-6 min-w-0">
                  <p className="text-xs font-body font-medium truncate" style={{ color: "var(--admin-container-text)" }}>
                    {entry.name}
                  </p>
                  {entry.reason && (
                    <p className="text-[10px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>
                      {entry.reason}
                    </p>
                  )}
                </div>
                <div className="col-span-3 flex items-center gap-1.5 text-[10px] font-body" style={{ color: "var(--admin-container-muted)" }}>
                  <ArrowRightLeft className="h-3 w-3" />
                  {entry.placement_count || 0} placement{entry.placement_count === 1 ? "" : "s"}
                </div>
                <div className="col-span-3 flex items-center justify-end gap-2">
                  {bucketKey === "converted" && entry.converted_to_survey_id && (
                    <button
                      onClick={() => nav(`/admin/surveys/${entry.converted_to_survey_id}`)}
                      className="text-[10px] font-body hover:underline"
                      style={{ color: "var(--primary-color)" }}
                      data-testid={`conv-view-survey-${entry.id}`}
                    >
                      View Survey →
                    </button>
                  )}
                  {(bucketKey === "auto_convert" || bucketKey === "review") && (
                    <>
                      <Button
                        size="sm" variant="outline" disabled={actingId === entry.id}
                        onClick={() => reject(entry)}
                        className="text-[10px] font-body h-7 gap-1"
                        style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}
                        data-testid={`conv-reject-${entry.id}`}
                      >
                        <XCircle className="h-3 w-3" /> Keep
                      </Button>
                      <Button
                        size="sm" disabled={actingId === entry.id}
                        onClick={() => setConfirmApprove(entry)}
                        className="text-[10px] font-body h-7 gap-1"
                        style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                        data-testid={`conv-approve-${entry.id}`}
                      >
                        {actingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                        Convert
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const autoCount = queue?.auto_convert?.length || 0;

  return (
    <div
      className="rounded-xl border"
      style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}
      data-testid="survey-conversion-section"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3.5 flex items-center justify-between gap-3 text-left"
        data-testid="survey-conversion-toggle"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" style={{ color: "var(--admin-container-muted)" }} />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "var(--admin-container-muted)" }} />
          )}
          <ArrowRightLeft className="h-4 w-4 shrink-0" style={{ color: "var(--primary-color)" }} />
          <div className="min-w-0">
            <h3 className="text-sm font-heading font-bold" style={{ color: "var(--admin-container-text)" }}>
              Legacy Assessment Conversion
            </h3>
            <p className="text-[11px] font-body mt-0.5" style={{ color: "var(--admin-container-muted)" }}>
              Migrate legacy assessments that functioned as surveys into first-class Survey objects.
            </p>
          </div>
        </div>
        {queue && (
          <div className="flex items-center gap-1.5 shrink-0">
            {autoCount > 0 && (
              <Badge
                className="text-[10px] font-body"
                style={{ backgroundColor: "color-mix(in srgb, #0d9488 15%, transparent)", color: "#0d9488", border: "1px solid #0d9488" }}
              >
                {autoCount} auto
              </Badge>
            )}
            {(queue.review?.length || 0) > 0 && (
              <Badge
                className="text-[10px] font-body"
                style={{ backgroundColor: "color-mix(in srgb, #d97706 15%, transparent)", color: "#d97706", border: "1px solid #d97706" }}
              >
                {queue.review.length} review
              </Badge>
            )}
          </div>
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-3 border-t" style={{ borderColor: "var(--admin-container-border)" }}>
          <div className="flex items-center justify-end gap-2 pt-3">
            <Button
              variant="outline" size="sm" onClick={fetchQueue} disabled={loading}
              className="text-[11px] font-body gap-1.5 h-7"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-text)" }}
              data-testid="refresh-queue-btn"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Refresh
            </Button>
            {autoCount > 0 && (
              <Button
                size="sm" onClick={autoApplyAll} disabled={autoApplying}
                className="text-[11px] font-body gap-1.5 h-7"
                style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
                data-testid="auto-apply-btn"
              >
                {autoApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                Auto-convert {autoCount}
              </Button>
            )}
          </div>

          <p className="text-[11px] font-body leading-relaxed" style={{ color: "var(--admin-container-muted)" }}>
            <strong style={{ color: "var(--admin-container-text)" }}>What happens on conversion:</strong> a new Survey is created with survey-compatible items (MC, SATA, Likert). Existing track placements are rewritten to point at the Survey. <strong>Historical responses are NOT migrated</strong> — they stay as historical assessment records. The source assessment is archived as <em>converted</em>.
          </p>

          {loading && !queue ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--primary-color)" }} />
            </div>
          ) : (
            <div className="space-y-2">
              {renderBucketSection("auto_convert")}
              {renderBucketSection("review")}
              {renderBucketSection("converted")}
              {renderBucketSection("keep")}
              {renderBucketSection("kept")}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!confirmApprove} onOpenChange={(o) => !o && setConfirmApprove(null)}>
        <DialogContent style={{ backgroundColor: "var(--admin-container-bg)", borderColor: "var(--admin-container-border)" }}>
          <DialogHeader>
            <DialogTitle className="font-heading" style={{ color: "var(--admin-container-text)" }}>
              Convert to Survey
            </DialogTitle>
            <DialogDescription className="font-body" style={{ color: "var(--admin-container-muted)" }}>
              Convert <strong style={{ color: "var(--admin-container-text)" }}>{confirmApprove?.name}</strong> to a Survey?
              <br /><br />
              {confirmApprove?.placement_count > 0 && (
                <>• <strong>{confirmApprove.placement_count}</strong> track placement{confirmApprove.placement_count === 1 ? "" : "s"} will be rewritten.<br /></>
              )}
              • {confirmApprove?.items || 0} compatible item{confirmApprove?.items === 1 ? "" : "s"} will be linked.<br />
              • Historical responses stay untouched.<br />
              • The source assessment is archived as <em>converted</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline" size="sm" onClick={() => setConfirmApprove(null)}
              className="text-xs font-body"
              style={{ borderColor: "var(--admin-container-border)", color: "var(--admin-container-muted)" }}
            >
              Cancel
            </Button>
            <Button
              size="sm" onClick={() => approve(confirmApprove)} disabled={actingId === confirmApprove?.id}
              className="text-xs font-body gap-1.5"
              style={{ backgroundColor: "var(--primary-color)", color: "var(--on-secondary, #FFFFFF)" }}
              data-testid="conv-confirm-btn"
            >
              {actingId === confirmApprove?.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Confirm Conversion
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
